import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TooltipDirective } from '../../../shared/components/ui/tooltip/tooltip.directive';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { faCopy, faArrowsRotate, faShieldHalved } from '@fortawesome/pro-duotone-svg-icons';
import {
  MIN_ROOM_PASSWORD_LENGTH,
  RoomPasswordStore,
} from '../../../state/room-passwords.repository';
import { TextField } from '../../../shared/components/form-fields/text-field/text-field';
import { TextFieldAction } from '../../../shared/components/form-fields/text-field/text-field.types';
import { MatToggleWrapper } from '../../../shared/components/material-wrappers/mat-toggle-wrapper/mat-toggle-wrapper';
import { ExpansionPanel } from '../../../shared/components/ui/expansion-panel/expansion-panel';
import { createRoomState } from '../room-state';
import { LanguageService } from '../../../core/services/language.service';

// Whether the password input acts like a creator-side editor (prefilled,
// visible plaintext, with copy + regenerate actions) or a joiner-side gate
// (empty, hidden, must be typed in).
type PasswordRole = 'creator' | 'joiner' | 'hidden';

/**
 * Self-contained end-to-end-encryption section for the pre-join screens and
 * the room dialog.
 *
 * In `mode="readonly"` (the room dialog editing an existing room) it only shows
 * the room's encryption state, with a copy action when this device holds the
 * key: the E2EE mode and password are fixed when a room is created.
 *
 * In `mode="auto"` it renders, depending on the classification of `roomName`:
 * - creator-ish rooms ('unknown' / 'preset-from-link'): an expansion panel
 *   with the opt-in e2e toggle and, when enabled, the editable plaintext
 *   password with copy / regenerate actions;
 * - joiner rooms ('known-with-password'): a bare password field — a required
 *   input is never hidden inside a collapsible — explaining why when the server
 *   refused our join (password missing or wrong);
 * - existing password rooms whose key is already stored
 *   ('known-with-stored-password', e.g. right after creating the room): a
 *   passive indicator with a copy action — no editing, since regenerating or
 *   opting out would desync the local key from the one invitees received;
 * - anything else: nothing, so parents can place it unconditionally.
 *
 * Parents must gate their join flow on `joinBlocked()` and call
 * `commitForJoin(roomName)` right before connecting; it persists the
 * password decision into `RoomPasswordStore` and returns false when joining
 * must be aborted (required password still missing).
 */
@Component({
  selector: 'andor-e2e-encryption-panel',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MatSlideToggle,
    TooltipDirective,
    MatToggleWrapper,
    TextField,
    ExpansionPanel,
    FaIconComponent,
  ],
  templateUrl: './e2e-encryption-panel.html',
  styleUrl: './e2e-encryption-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class E2eEncryptionPanel {
  // Deliberately not `input.required`: parents read `joinBlocked()` through
  // `viewChild` from bindings that evaluate before this component's input
  // binding executes on the creation pass — a required input would throw
  // NG0950 there. An empty room name degrades to the 'unknown' room state.
  roomName = input('');

  /**
   * `'auto'` classifies the room and picks a creator / joiner / read-only
   * presentation; `'readonly'` only shows an existing room's fixed encryption
   * state (see the class doc).
   */
  mode = input<'auto' | 'readonly'>('auto');

  /**
   * Start a room without a stored password encrypted, with a generated
   * password (the create dialog: new rooms are end-to-end encrypted unless the
   * creator opts out). Join and edit usages leave it off.
   */
  defaultEncrypted = input(false);

  // Local toggle for the "Use end-to-end encryption" choice. Defaults to
  // `defaultEncrypted`; flipped on automatically when arriving with a preset
  // password from an invite link.
  protected e2eEnabled = signal(false);
  // Whether the expansion panel is open. Auto-opens when the user lands
  // with a preset password so the prefilled state is visible immediately.
  protected panelExpanded = signal(false);
  protected passwordControl = new FormControl('', []);

  protected readonly faShieldHalved = faShieldHalved;
  protected readonly faCopy = faCopy;

  private readonly _roomPasswords = inject(RoomPasswordStore);
  private readonly _clipboard = inject(Clipboard);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _translate = inject(TranslateService);
  // Read before `instant()` so labels re-translate when the language changes.
  private readonly _lang = inject(LanguageService).active;

  readonly roomState = createRoomState(this.roomName);

  private readonly _readonly = computed(() => this.mode() === 'readonly');

  /**
   * What the read-only presentation shows: encrypted with the key saved on
   * this device, encrypted without it, or not encrypted. `null` outside
   * `'readonly'` mode.
   */
  protected readonly readonlyState = computed<'stored' | 'encrypted' | 'open' | null>(() => {
    if (!this._readonly()) return null;
    const state = this.roomState();
    if (state === 'known-with-stored-password') return 'stored';
    return state === 'known-with-password' ? 'encrypted' : 'open';
  });

  // The toggle is shown for new rooms AND for invite-link landings (so the
  // user can still opt out / regenerate before joining).
  protected readonly showE2eToggle = computed(() => {
    if (this._readonly()) return false;
    const state = this.roomState();
    return state === 'unknown' || state === 'preset-from-link';
  });

  private readonly _passwordRole = computed<PasswordRole>(() => {
    if (this._readonly()) return 'hidden';
    const state = this.roomState();
    if (state === 'known-with-password') return 'joiner';
    if ((state === 'unknown' || state === 'preset-from-link') && this.e2eEnabled()) {
      return 'creator';
    }
    return 'hidden';
  });

  protected readonly showPasswordField = computed(() => this._passwordRole() !== 'hidden');
  // The join-flow indicator for a key we already hold; the read-only mode
  // shows its own state line instead.
  protected readonly showStoredIndicator = computed(
    () => !this._readonly() && this.roomState() === 'known-with-stored-password'
  );

  /** Why the server refused our last join of this room, shown at the joiner field. */
  protected readonly rejection = computed(() =>
    this._passwordRole() === 'joiner'
      ? this._roomPasswords.rejection(this.roomName().trim())
      : undefined
  );
  protected readonly passwordInputType = computed(() =>
    this._passwordRole() === 'creator' ? 'text' : 'password'
  );

  private readonly _passwordValue = toSignal(this.passwordControl.valueChanges, {
    initialValue: this.passwordControl.value,
  });

  /**
   * The password the field was seeded with from the store — typically an
   * invite link's. It is exempt from the creator minimum: the room may already
   * exist (hidden rooms are never listed), possibly with a shorter password.
   */
  private readonly _seededPassword = signal('');

  /**
   * A password the user typed or edited for a new encrypted room is shorter
   * than {@link MIN_ROOM_PASSWORD_LENGTH}. Joiner passwords are never checked.
   */
  protected readonly passwordTooShort = computed(() =>
    this._isTooShort(this._passwordValue() ?? '')
  );

  protected readonly passwordErrors = computed(() => {
    this._lang();
    return {
      tooShort: this._translate.instant('The password must be at least [count] characters long.', {
        count: MIN_ROOM_PASSWORD_LENGTH,
      }),
    };
  });

  // Parents must consult this in their join-disabled state: joining is
  // blocked while a visible password field is empty (joiner gate, or a
  // creator who cleared the generated password) or a chosen creator password
  // is too short.
  readonly joinBlocked = computed(
    () => this.showPasswordField() && (!this._passwordValue() || this.passwordTooShort())
  );

  private readonly _passwordInlineActions = computed<TextFieldAction[]>(() => {
    this._lang();
    return [
      {
        icon: faCopy,
        tooltip: this._translate.instant('Copy password'),
        ariaLabel: this._translate.instant('Copy password'),
        click: (value) => this._copyPassword(value),
      },
      {
        icon: faArrowsRotate,
        tooltip: this._translate.instant('Generate a new password'),
        ariaLabel: this._translate.instant('Generate a new password'),
        click: () => {
          this.passwordControl.setValue(this._roomPasswords.generate());
          this.passwordControl.markAsDirty();
        },
      },
    ];
  });

  // Only show the inline actions when the user is acting as the creator of
  // a brand-new (or preset-from-link) encrypted room — not when they're
  // typing a joiner password for an existing protected room.
  protected readonly creatorActions = computed(() =>
    this._passwordRole() === 'creator' ? this._passwordInlineActions() : []
  );

  constructor() {
    // Reconcile the toggle + password field with whatever the password store
    // already holds whenever the room name changes:
    //   - landing with a preset password (from a #p= invite link) flips the
    //     toggle ON, prefills the field, and opens the panel;
    //   - switching to a room name we've stored a password for restores the
    //     same state;
    //   - switching to a fresh room name resets back to "off, empty", or to
    //     "on" with a generated password when `defaultEncrypted`.
    // The store is read untracked: keyed on the room name only, so a store
    // commit (this room's or another's) never clobbers in-progress edits.
    effect(() => {
      const name = this.roomName().trim();
      untracked(() => {
        const stored = name ? this._roomPasswords.get(name) : undefined;
        this._seededPassword.set(stored ?? '');
        if (stored) {
          this.e2eEnabled.set(true);
          this.passwordControl.setValue(stored);
          this.panelExpanded.set(true);
        } else if (this.defaultEncrypted()) {
          this.e2eEnabled.set(true);
          this.passwordControl.setValue(this._roomPasswords.generate());
        } else {
          this.e2eEnabled.set(false);
          this.passwordControl.setValue('');
        }
      });
    });

    // Surface the creator minimum through the text field's own error display.
    // Added here rather than at construction of the control, as the validator
    // reads the role and seed signals declared after it.
    this.passwordControl.addValidators((control) =>
      this._isTooShort(control.value ?? '') ? { tooShort: true } : null
    );
    // The role and the seed change without the value changing: revalidate.
    effect(() => {
      this._passwordRole();
      this._seededPassword();
      untracked(() => this.passwordControl.updateValueAndValidity());
    });
  }

  private _isTooShort(value: string): boolean {
    return (
      this._passwordRole() === 'creator' &&
      value.length > 0 &&
      value !== this._seededPassword() &&
      Array.from(value).length < MIN_ROOM_PASSWORD_LENGTH
    );
  }

  /** Copy the stored key from the passive indicator (out-of-band sharing). */
  protected copyStoredPassword(): void {
    this._copyPassword(this._roomPasswords.get(this.roomName().trim()));
  }

  private _copyPassword(value: string | null | undefined): void {
    if (!value) return;
    this._clipboard.copy(value);
    this._snackBar.open(this._translate.instant('Password copied to clipboard.'), undefined, {
      duration: 2500,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }

  protected toggleE2e() {
    const next = !this.e2eEnabled();
    this.e2eEnabled.set(next);
    if (next) {
      // Flipped on: prefill with a freshly generated password unless the
      // user already has something typed in the field.
      if (!this.passwordControl.value) {
        this.passwordControl.setValue(this._roomPasswords.generate());
      }
    } else {
      // Flipped off: clear the field AND drop any stored password for this
      // room — the user just opted out of encryption.
      this.passwordControl.setValue('');
      const name = this.roomName().trim();
      if (name) this._roomPasswords.remove(name);
    }
  }

  /**
   * Resolve the password for a NEW room being created, WITHOUT persisting it —
   * the roomId doesn't exist yet, so the caller stores it under the
   * server-assigned id (via `OdinService.createRoom`). Returns the custom or
   * freshly generated password when e2e is enabled, else null.
   */
  resolveCreatePassword(): string | null {
    if (!this.e2eEnabled()) return null;
    return this.passwordControl.value || this._roomPasswords.generate();
  }

  /**
   * Persist the password decision for `roomName` into the store; called by
   * the parent right before connecting. Returns false when joining must be
   * aborted because a required password is missing.
   */
  commitForJoin(roomName: string): boolean {
    const password = this.passwordControl.value;
    const state = this.roomState();
    if (state === 'known-with-stored-password') {
      // The key is already persisted; nothing to edit, nothing to commit.
      return true;
    }
    if (state === 'known-with-password') {
      if (!password) return false; // join gate should already prevent this; defensive
      this._roomPasswords.set(roomName, password);
    } else if ((state === 'unknown' || state === 'preset-from-link') && this.e2eEnabled()) {
      // Creator path (or invite-link landing that opted to keep encryption):
      // commit whatever password is in the field — could be the generated
      // value, the URL-preset value, or a user-typed custom one.
      this._roomPasswords.set(roomName, password || this._roomPasswords.generate());
    } else if (state === 'preset-from-link' && !this.e2eEnabled()) {
      // User landed with a preset link password but opted out of e2e.
      // Already removed in toggleE2e(), but be defensive.
      this._roomPasswords.remove(roomName);
    }
    // 'known-without-password' / 'unknown' with toggle off → nothing to do.
    return true;
  }
}
